import { useState } from 'react';
import { useParams, useLocation, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import QUIZ_THEMES from '../../utils/quizThemes';
import { useCallback } from 'react';
import { Swords, Award, Trophy, Loader2 } from 'lucide-react';

export default function ResultsPage() {
  const { t, i18n } = useTranslation();
  const [lang, setLang] = useState(() => i18n.language === 'al' ? 'al' : 'de');
  const toggleLang = useCallback(() => {
    const next = lang === 'de' ? 'al' : 'de';
    setLang(next);
    i18n.changeLanguage(next);
  }, [lang, i18n]);
  const { sessionId } = useParams();
  const { state } = useLocation();

  const {
    leaderboard = [],
    teamScores = null,
    isDuel = false,
    score = 0,
    participant,
    participantId,
    recap = [],
    recapEnabled = false,
  } = state || {};

  const theme = QUIZ_THEMES[state?.bgColor] || QUIZ_THEMES.dark;
  const bgImage = state?.bgImage || null;
  const outerBg = bgImage
    ? { backgroundImage: `url(${bgImage})`, backgroundSize: 'cover', backgroundPosition: 'center' }
    : { background: theme.bg };
  const overlayStyle = bgImage
    ? { minHeight: '100vh', background: 'rgba(0,0,0,0.78)', padding: '32px 20px', color: '#fff' }
    : { minHeight: '100vh', padding: '32px 20px', color: theme.text };
  const liveText = bgImage ? '#fff' : theme.text;
  // Cards need higher contrast over a background photo
  const cardBg = bgImage ? 'rgba(20,20,20,0.72)' : theme.surface;
  const cardBorder = bgImage ? 'rgba(255,255,255,0.28)' : theme.border;

  const [tab, setTab] = useState('individual');
  const [certLoading, setCertLoading] = useState(false);
  const [showRecap, setShowRecap] = useState(false);

  const myRank = leaderboard.findIndex(
    p => p.firstName === participant?.firstName && p.lastName === participant?.lastName
  ) + 1;

  const tabs = [
    { key: 'individual', label: t('quiz.leaderboard') },
    ...(isDuel && teamScores ? [{ key: 'duel', label: 'Duell' }] : []),
  ];

  const downloadCert = async () => {
    if (!participantId || !sessionId) return;
    setCertLoading(true);
    try {
      const res = await fetch(`/api/public/certificate/${sessionId}/${participantId}`);
      if (!res.ok) throw new Error('Fehler');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Zertifikat-${participant?.firstName || 'Teilnehmer'}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      alert('Zertifikat konnte nicht geladen werden.');
    } finally {
      setCertLoading(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh', ...outerBg }}>
      <div style={overlayStyle}>
        <div style={{ maxWidth: 480, margin: '0 auto' }}>

          {/* Header */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
            <button
              onClick={toggleLang}
              style={{
                background: bgImage ? 'rgba(255,255,255,0.15)' : theme.surface,
                border: `1px solid ${bgImage ? 'rgba(255,255,255,0.25)' : theme.border}`,
                color: liveText, borderRadius: 8, padding: '4px 12px',
                fontSize: 12, fontWeight: 700, cursor: 'pointer', letterSpacing: 0.5,
              }}
            >
              {lang === 'de' ? 'DE' : 'AL'}
            </button>
          </div>
          <div style={{ textAlign: 'center', marginBottom: 32 }}>
            <h1 style={{ fontFamily: 'Playfair Display', fontSize: 32, color: theme.accent, marginBottom: 8 }}>
              {t('quiz.finalResults')}
            </h1>
            {myRank > 0 && myRank <= 3 && (
              <p style={{ fontSize: 18, color: '#C9A84C', fontWeight: 700 }}>{t('quiz.congratulations')}</p>
            )}
          </div>

          {/* Score card */}
          <div style={{
            background: bgImage ? 'rgba(255,255,255,0.1)' : theme.surface,
            border: `1px solid ${bgImage ? 'rgba(255,255,255,0.2)' : theme.border}`,
            borderRadius: 16, padding: '24px 20px', textAlign: 'center', marginBottom: 20,
          }}>
            <p style={{ fontSize: 14, color: `${liveText}99`, marginBottom: 8 }}>{t('quiz.yourScore')}</p>
            <div style={{ fontSize: 56, fontWeight: 800, color: theme.accent, fontFamily: 'Playfair Display' }}>{score}</div>
            <p style={{ color: `${liveText}99` }}>{t('common.points')}</p>
            {myRank > 0 && (
              <div style={{ marginTop: 12, fontSize: 22, fontWeight: 700, color: liveText }}>
                {t('common.rank')}: #{myRank}
              </div>
            )}

            {/* Certificate download */}
            {participantId && sessionId && (
              <button
                onClick={downloadCert}
                disabled={certLoading}
                style={{
                  marginTop: 18,
                  background: bgImage ? 'rgba(201,168,76,0.9)' : '#C9A84C',
                  color: '#1a1a1a',
                  border: 'none', borderRadius: 8,
                  padding: '10px 22px', fontSize: 14, fontWeight: 700,
                  cursor: certLoading ? 'default' : 'pointer',
                  opacity: certLoading ? 0.7 : 1,
                  letterSpacing: 0.5,
                }}
              >
                {certLoading
                  ? <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}><Loader2 size={14} className="spin" /> Lädt...</span>
                  : <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}><Award size={14} /> Zertifikat herunterladen</span>
                }
              </button>
            )}
          </div>

          {/* Tab switcher */}
          {tabs.length > 1 && (
            <div style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
              {tabs.map(tb => (
                <button
                  key={tb.key}
                  onClick={() => setTab(tb.key)}
                  style={{
                    padding: '7px 16px', borderRadius: 20, fontSize: 13, fontWeight: 600,
                    cursor: 'pointer', border: 'none',
                    background: tab === tb.key
                      ? (bgImage ? 'rgba(91,138,117,0.9)' : theme.accent)
                      : (bgImage ? 'rgba(255,255,255,0.12)' : theme.surface),
                    color: tab === tb.key ? '#fff' : liveText,
                  }}
                >
                  {tb.label}
                </button>
              ))}
            </div>
          )}

          {/* Individual leaderboard */}
          {tab === 'individual' && (
            <div style={{
              background: bgImage ? 'rgba(255,255,255,0.1)' : theme.surface,
              border: `1px solid ${bgImage ? 'rgba(255,255,255,0.2)' : theme.border}`,
              borderRadius: 16, padding: 16, marginBottom: 24,
            }}>
              <h3 style={{ fontSize: 14, color: `${liveText}99`, marginBottom: 12 }}>{t('quiz.leaderboard')}</h3>
              {leaderboard.map((p, i) => (
                <div key={i} style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  padding: '10px 0',
                  borderBottom: i < leaderboard.length - 1 ? `1px solid ${bgImage ? 'rgba(255,255,255,0.1)' : theme.border}` : 'none',
                  fontWeight: p.firstName === participant?.firstName && p.lastName === participant?.lastName ? 700 : 400,
                }}>
                  <div style={{
                    width: 28, textAlign: 'center', fontWeight: 700,
                    color: i === 0 ? '#C9A84C' : i === 1 ? '#aaa' : i === 2 ? '#C17B5C' : `${liveText}66`,
                    fontSize: i < 3 ? 18 : 14,
                  }}>
                    {p.rank}
                  </div>
                  <div style={{ flex: 1, color: liveText }}>
                    {p.firstName} {p.lastName}
                    {p.team && <span style={{ fontSize: 11, opacity: 0.6, marginLeft: 6 }}>({p.team})</span>}
                  </div>
                  <div style={{ fontWeight: 700, color: theme.accent }}>{p.score} Pkt.</div>
                </div>
              ))}
            </div>
          )}

          {/* Duel / team scores */}
          {tab === 'duel' && teamScores && (
            <div style={{
              background: bgImage ? 'rgba(255,255,255,0.1)' : theme.surface,
              border: `1px solid ${bgImage ? 'rgba(255,255,255,0.2)' : theme.border}`,
              borderRadius: 16, padding: 16, marginBottom: 24,
            }}>
              <h3 style={{ fontSize: 14, color: `${liveText}99`, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 6 }}>
                <Swords size={14} /> Duell-Ergebnis
              </h3>
              {teamScores.map((ts, i) => (
                <div key={i} style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '14px 16px', marginBottom: 8, borderRadius: 12,
                  background: i === 0
                    ? (bgImage ? 'rgba(91,138,117,0.3)' : `${theme.accent}22`)
                    : (bgImage ? 'rgba(255,255,255,0.08)' : `${theme.surface}88`),
                  border: `2px solid ${i === 0 ? theme.accent : (bgImage ? 'rgba(255,255,255,0.15)' : theme.border)}`,
                }}>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 16, color: liveText, display: 'flex', alignItems: 'center', gap: 6 }}>
                      {i === 0 && <Trophy size={16} color="#C9A84C" />}{ts.team}
                    </div>
                    <div style={{ fontSize: 12, color: `${liveText}77` }}>{ts.members} Teilnehmer</div>
                  </div>
                  <div style={{ fontSize: 24, fontWeight: 800, color: i === 0 ? theme.accent : liveText }}>
                    {ts.score} Pkt.
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Rueckblick / Recap — hinter einem Button */}
          {recapEnabled && recap.length > 0 && (
            <div style={{ marginBottom: 24 }}>
              <button
                type="button"
                onClick={() => setShowRecap(s => !s)}
                className="btn btn-secondary"
                style={{ width: '100%', marginBottom: showRecap ? 12 : 0 }}
              >
                {showRecap
                  ? (lang === 'de' ? 'Rueckblick ausblenden' : 'Mbyll permbledhjen')
                  : (lang === 'de' ? 'Rueckblick — Richtige Antworten anzeigen' : 'Shiko permbledhjen — Pergjigjet e sakta')}
              </button>
              {showRecap && (
                <div style={{ background: cardBg, border: `1px solid ${cardBorder}`, borderRadius: 16, padding: 16 }}>
                  {recap.map((it, qi) => {
                    const q = it.q || {};
                    const qtext = lang === 'de' ? q.text_de : (q.text_al || q.text_de);
                    return (
                      <div key={qi} style={{ marginBottom: 14, paddingBottom: 12, borderBottom: qi < recap.length - 1 ? `1px solid ${bgImage ? 'rgba(255,255,255,0.15)' : theme.border}` : 'none' }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: liveText, marginBottom: 8 }}>{qi + 1}. {qtext}</div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                          {['a', 'b', 'c', 'd'].filter(k => q[`option_${k}_de`]).map(k => {
                            const isCorrect = it.correctOption === k;
                            const otext = lang === 'de' ? q[`option_${k}_de`] : (q[`option_${k}_al`] || q[`option_${k}_de`]);
                            return (
                              <div key={k} style={{ fontSize: 13, padding: '6px 10px', borderRadius: 8, background: isCorrect ? '#5B8A75' : (bgImage ? 'rgba(255,255,255,0.1)' : theme.bg), color: isCorrect ? '#fff' : liveText, fontWeight: isCorrect ? 600 : 400 }}>
                                {k.toUpperCase()}. {otext}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Share */}
          {myRank > 0 && (
            <div style={{ textAlign: 'center', marginBottom: 16 }}>
              <a
                href={`https://wa.me/?text=${encodeURIComponent(`Ich habe am islamischen Quiz teilgenommen und Platz ${myRank} belegt — ${score} Punkte. Nächstes Quiz auf ilmify.ch`)}`}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 8,
                  background: '#25D366', color: '#fff',
                  borderRadius: 8, padding: '10px 20px', fontSize: 14, fontWeight: 700,
                  textDecoration: 'none',
                }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/><path d="M12 0C5.373 0 0 5.373 0 12c0 2.123.554 4.116 1.524 5.845L0 24l6.335-1.502A11.94 11.94 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 22c-1.891 0-3.659-.507-5.187-1.388l-.37-.22-3.762.892.946-3.665-.242-.38A9.955 9.955 0 012 12C2 6.477 6.477 2 12 2s10 4.477 10 10-4.477 10-10 10z"/></svg>
                Ergebnis teilen
              </a>
            </div>
          )}

          <div style={{ textAlign: 'center' }}>
            <Link to="/join" className="btn btn-primary btn-lg">Neues Quiz beitreten</Link>
          </div>

        </div>
      </div>
    </div>
  );
}
